import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface ViduQ2Params {
  /**
   * 文本提示词，必填，长度不超过1500字符
   */
  prompt: string;
  /**
   * 视频时长（秒），默认5秒，可选1-8秒
   * @default 5
   * @range 1 - 8
   */
  duration?: number;
  /**
   * 视频分辨率，默认720p，可选值：360p、540p、720p、1080p
   * @default "720p"
   * @values 360p | 540p | 720p | 1080p
   */
  resolution?: '360p' | '540p' | '720p' | '1080p';
  /**
   * 随机种子，默认0（随机）
   * @default 0
   */
  seed?: number;
  /**
   * 宽高比，默认16:9，可选值：16:9、9:16、1:1
   * @default "16:9"
   * @values 16:9 | 9:16 | 1:1
   */
  aspect_ratio?: '16:9' | '9:16' | '1:1';
  /**
   * 运动幅度，默认auto，可选值：auto、small、medium、large
   * @default "auto"
   * @values auto | small | medium | large
   */
  movement_amplitude?: 'auto' | 'small' | 'medium' | 'large';
  /**
   * 是否添加背景音乐，默认false
   * @default false
   */
  bgm?: boolean;
  /**
   * 透传参数，最大1048576字符
   */
  payload?: string;
  /**
   * 低峰模式，默认false
   * @default false
   */
  off_peak?: boolean;
  /**
   * 是否添加水印，默认false
   * @default false
   */
  wartermark?: boolean;
  /**
   * 回调URL，必须以http://或https://开头
   */
  callback_url?: string;
}

export interface ViduQ2Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * vidu_q2
 *
 * Vidu Q2 Text-to-Video generation implementation.
 * 
 * API Documentation: https://platform.vidu.com/docs/text-to-video
 * Status API: https://platform.vidu.com/docs/get-generation
 *
 * @example
 * ```typescript
 * import { initSeacloud, viduQ2 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await viduQ2({
 *   // 参数见 ViduQ2Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 ViduQ2Params 接口定义
 * @returns 生成的资源列表
 */
export async function viduQ2(
  params: ViduQ2Params,
): Promise<ViduQ2Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'vidu_q2',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: ViduQ2Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
